import { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import VehicleAddress from './VehicleAddress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TrajectoryTimelineProps {
  trips: any[];
  tripPositions: Array<{
    id: string;
    fix_time: string;
    lat: number;
    lng: number;
    speed_kmh: number | null;
    ignition: boolean | null;
  }>;
  onTripSelect: (tripId: string) => void;
  selectedTripId: string | null;
  companyId: string;
  deviceId: string;
  dateRange: { start: string; end: string };
}

export function TrajectoryTimeline({
  trips,
  tripPositions,
  onTripSelect,
  selectedTripId,
  companyId,
  deviceId,
  dateRange,
}: TrajectoryTimelineProps) {
  // Buscar TODAS as posições do device no período selecionado
  const { data: allPositions = [], isLoading } = useQuery({
    queryKey: ['gps-trajectory', deviceId, dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gps_positions')
        .select('id, fix_time, lat, lng, speed_kmh, ignition')
        .eq('device_id', deviceId)
        .gte('fix_time', dateRange.start)
        .lte('fix_time', dateRange.end)
        .order('fix_time', { ascending: false })
        .limit(500);

      if (error) throw error;
      return data || [];
    },
    enabled: !!deviceId && !!dateRange.start && !!dateRange.end,
  });

  // Gerar eventos a partir de TODAS as posições do período
  const events = useMemo(() => {
    if (!allPositions || allPositions.length === 0) {
      return [];
    }

    return allPositions.map((position) => {
      const isMoving = (position.speed_kmh || 0) > 5;
      
      return {
        id: position.id,
        timestamp: position.fix_time,
        lat: position.lat,
        lng: position.lng,
        speed: position.speed_kmh || 0,
        status: isMoving ? 'Em Movimento' : 'Parado',
        color: isMoving ? 'bg-green-500' : 'bg-orange-500',
      };
    });
  }, [allPositions]);

  return (
    <ScrollArea className="h-full">
      <div className="px-4 py-2 space-y-2">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Carregando trajeto...
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Nenhum ponto encontrado no período selecionado
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-2">
              Mostrando {events.length} pontos
            </div>
            {events.map((event) => (
              <div
                key={event.id}
                className="flex items-start gap-3 p-3 rounded-lg border bg-background"
              >
                {/* Bolinha colorida indicadora */}
                <div
                  className={`h-3 w-3 rounded-full mt-1 flex-shrink-0 ${event.color} shadow-sm`}
                />

                {/* Conteúdo do evento */}
                <div className="flex-1 min-w-0 space-y-1">
                  {/* Timestamp e status */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">
                      {formatTime(event.timestamp)}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {event.status} • {event.speed.toFixed(0)} km/h
                    </Badge>
                  </div>

                  {/* Endereço */}
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    <VehicleAddress
                      latitude={event.lat}
                      longitude={event.lng}
                      companyId={companyId}
                    />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </ScrollArea>
  );
}

// Helper function
function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
